import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import axios from 'axios';
import AnimatedBackground from '../components/AnimatedBackground';

const API_URL = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CartPage() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart, getCartTotal } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    setIsCheckingOut(true);
    try {
      // For now, redirect to first item's detail page for checkout
      // In a real implementation, you'd create a bulk checkout flow
      if (cartItems.length === 1) {
        navigate(`/mod/${cartItems[0].id}`);
      } else {
        // For multiple items, navigate to first item
        // You could implement a multi-item checkout here
        navigate(`/mod/${cartItems[0].id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setIsCheckingOut(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#020403] relative overflow-hidden">
        {/* Animated Background - Coding/Tech Theme */}
        <AnimatedBackground
          imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
          opacity={0.5}
        />

        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen gap-6 px-4">
          <div className="flex items-center justify-center w-32 h-32 rounded-full bg-white/5 border-2 border-white/10">
            <span className="material-symbols-outlined text-6xl text-gray-600">shopping_cart</span>
          </div>
          <h1 className="text-4xl font-bold text-white tracking-widest uppercase" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Your Cart is Empty
          </h1>
          <p className="text-gray-400 font-mono text-sm">&gt; Add some digital assets to get started</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-8 py-4 bg-green-500 hover:bg-white text-black font-bold uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:shadow-[0_0_25px_#00ff41]"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020403] relative">
      {/* Animated Background - Coding/Tech Theme */}
      <AnimatedBackground
        imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
        opacity={0.5}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="material-symbols-outlined text-green-500 text-3xl">shopping_cart</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-widest uppercase" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Shopping Cart
            </h1>
          </div>
          <p className="text-gray-400 font-mono text-sm">&gt; {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-[#0a110e] border border-blue-400/20 p-6 holo-card flex flex-col md:flex-row gap-6"
                style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)'}}
              >
                {/* Product Image */}
                <div
                  className="w-full md:w-48 aspect-video bg-black border border-white/10 grayscale hover:grayscale-0 transition-all cursor-pointer"
                  style={{
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    clipPath: 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)'
                  }}
                  onClick={() => navigate(`/mod/${item.id}`)}
                />

                {/* Product Info */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3
                          className="text-xl font-bold text-white hover:text-green-500 transition-colors cursor-pointer"
                          style={{fontFamily: 'Space Grotesk, sans-serif'}}
                          onClick={() => navigate(`/mod/${item.id}`)}
                        >
                          {item.name}
                        </h3>
                        <p className="text-gray-500 text-xs font-mono mt-1">&gt; By {item.author}</p>
                      </div>
                      <span className="bg-green-500/20 border border-green-500 text-green-500 px-2 py-1 text-xs font-bold uppercase tracking-wider">
                        {item.category}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm font-mono mb-4 border-l-2 border-white/10 pl-3">
                      &gt; {item.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-green-500 font-mono">
                      {formatPrice(item.price)}
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="px-4 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 font-mono uppercase text-xs transition-all flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary - Sticky */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div
                className="bg-[#0a110e] border border-green-500/30 p-6 shadow-2xl holo-card"
                style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 30px), calc(100% - 30px) 100%, 0 100%)'}}
              >
                <h2 className="text-xl font-bold text-white mb-6 uppercase tracking-widest" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6 pb-6 border-b border-white/10">
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-gray-400">Subtotal ({cartItems.length} items)</span>
                    <span className="text-white">{formatPrice(getCartTotal())}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-gray-400">Processing Fee</span>
                    <span className="text-white">$0.00</span>
                  </div>
                </div>

                <div className="flex justify-between text-lg font-bold mb-6">
                  <span className="text-white font-mono uppercase">Total</span>
                  <span className="text-green-500 text-2xl font-mono">{formatPrice(getCartTotal())}</span>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleCheckout}
                    disabled={isCheckingOut}
                    className="w-full h-14 bg-green-500 hover:bg-white text-black font-bold uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:shadow-[0_0_25px_#00ff41] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined">credit_card</span>
                    {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
                  </button>

                  <button
                    onClick={() => navigate('/')}
                    className="w-full h-12 border border-white/20 text-white font-mono hover:bg-white/5 transition-all uppercase text-sm"
                  >
                    Continue Shopping
                  </button>

                  <button
                    onClick={clearCart}
                    className="w-full h-10 text-red-400 hover:text-red-300 font-mono text-xs uppercase transition-all"
                  >
                    Clear Cart
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                    <span className="text-green-500">✓</span>
                    <span>Secure Stripe Payments</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                    <span className="text-green-500">✓</span>
                    <span>Instant Download Access</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                    <span className="text-green-500">✓</span>
                    <span>24/7 Support Available</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CartPage;
